import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { formatFromDate } from "src/app/common/datetime_utility";
import { getPlatformDisplayName } from "src/app/common/platformUtils";
import { beautifyCamelCaseString } from "src/app/common/stringUtils";
import { EmailData } from "src/app/components/actions/action-send-email/action-send-email.component";
import { IntegrationModel, Platform, ResourceType } from "src/app/models/api";
import {
    InventoryApplicationResourceProperties,
    InventoryEntityFieldStatus,
    InventoryResourceModel,
    PowerPlatformApplicationQuarantineStatus,
    ResourceStatus,
} from "src/app/models/api/inventoryResource.model";
import { PermissionEnum } from "src/app/models/auth0/permission.enum";
import { MenuAction } from "src/app/models/menuAction.model";
import { LabelsProviderPipe } from "src/app/pipes/labels-provider/labels-provider.pipe";
import { environment } from "src/environments/environment";
import {
    ActionResponseModel,
    ActionsHttpServiceService,
    AutomationActivationActions,
    AutomationActivationParams,
    PpApplicationQuarantineStatus,
    RequiredParamsForActionsHttpRequests,
    RequiredParamsForSetAccessHttpRequests,
    ResourceActionsResponseModel,
    SetApplicationQuarantineStateParamsModel,
} from "../../httpServices/actionsHttpService/actions-http-service.service";
import { PermissionsService } from "../../infra/permissionsService/permissions.service";
import { StoreService } from "../../infra/store/store.service";
import { arePlatformActionsAllowed } from "../common/actionsHelpers";
import { CommonActionsService, EmailPlaceholder } from "../common/common-actions.service";
import { InventorySupportedActions } from "./inventorySupportedActions";
import { IInventoryResourceViewModel } from "src/app/models/viewModels/inventoryResourceViewModel";
import { JsonHalCollection } from "src/app/models/api/jsonhal.model";
import _ from "lodash";

@Injectable({
    providedIn: "root",
})
export class InventoryActionsService {
    private _integrations: IntegrationModel[] = [];
    private _inventoryEmailPlaceholders: EmailPlaceholder[];
    private _actionIdActionModelDictionary: {
        [actionId: string]: InventorySupportedActions;
    } = {
        powerplatformQuarantineCanvasApp: InventorySupportedActions.Quarantine,
        deletePlatformResource: InventorySupportedActions.Delete,
        listResourcePermissions: InventorySupportedActions.ListAccess,
        downloadResourceAttachment: InventorySupportedActions.GetResourceSbomFile,
        powerplatformStopFlow: InventorySupportedActions.Stop,
        getRawResource: InventorySupportedActions.GetRawResource,
        powerplatformSetCopilotAuthentication: InventorySupportedActions.PowerplatformSetCopilotAuthentication,
        powerplatformGetRawFlowRuns: InventorySupportedActions.PowerplatformGetRawFlowRuns,
        powerplatformAgentDeleteAction: InventorySupportedActions.PowerplatformAgentDeleteAction,
        powerplatformAgentActionDeleteAction: InventorySupportedActions.PowerplatformAgentActionDeleteAction,
        powerplatformAgentActionDisableAction: InventorySupportedActions.PowerplatformAgentActionDisableAction,
        powerplatformAgentActionEnforceUserAuthenticationAction: InventorySupportedActions.PowerplatformAgentActionEnforceUserAuthenticationAction,
        powerplatformAgentActionEnforceUserConfirmationAction: InventorySupportedActions.PowerplatformAgentActionEnforceUserConfirmationAction,
        chatGptEnterpriseDeleteGptAction: InventorySupportedActions.ChatGptEnterpriseDeleteGptAction,
        chatGptEnterpriseIsolateGptAction: InventorySupportedActions.ChatGptEnterpriseIsolateGptAction,
        chatGptEnterpriseDeleteGptKnowledgeAction: InventorySupportedActions.ChatGptEnterpriseDeleteGptKnowledgeAction,
        chatGptEnterpriseGetGptKnowledgeFileUrlAction: InventorySupportedActions.ChatGptEnterpriseGetGptKnowledgeFileUrlAction,
        chatGptEnterpriseDeleteUserCanvasAction: InventorySupportedActions.ChatGptEnterpriseDeleteUserCanvasAction,
        chatGptEnterpriseGetUserCanvasAction: InventorySupportedActions.ChatGptEnterpriseGetUserCanvasAction,
    };

    constructor(
        private _commonActionsService: CommonActionsService,
        private _permissionsService: PermissionsService,
        private _store: StoreService,
        private _labelProvider: LabelsProviderPipe,
        private _httpActionsServiceService: ActionsHttpServiceService,
    ) {
        this._store.integrations$.subscribe((integrations: IntegrationModel[]) => {
            this._integrations = integrations;
        });
        this._inventoryEmailPlaceholders = [
            {
                label: this._labelProvider.transform("emailTemplatePlaceholders.resourceName.label"),
                description: this._labelProvider.transform("emailTemplatePlaceholders.resourceName.description"),
            },
            {
                label: this._labelProvider.transform("emailTemplatePlaceholders.resourceId.label"),
                description: this._labelProvider.transform("emailTemplatePlaceholders.resourceId.description"),
            },
            {
                label: this._labelProvider.transform("emailTemplatePlaceholders.resourceCategory.label"),
                description: this._labelProvider.transform("emailTemplatePlaceholders.resourceCategory.description"),
            },
            {
                label: this._labelProvider.transform("emailTemplatePlaceholders.resourceType.label"),
                description: this._labelProvider.transform("emailTemplatePlaceholders.resourceType.description"),
            },
            {
                label: this._labelProvider.transform("emailTemplatePlaceholders.environmentName.label"),
                description: this._labelProvider.transform("emailTemplatePlaceholders.environmentName.description"),
            },
            {
                label: this._labelProvider.transform("emailTemplatePlaceholders.environmentType.label"),
                description: this._labelProvider.transform("emailTemplatePlaceholders.environmentType.description"),
            },
            {
                label: this._labelProvider.transform("emailTemplatePlaceholders.inventoryResourceLink.label"),
                description: this._labelProvider.transform("emailTemplatePlaceholders.inventoryResourceLink.description"),
            },
            {
                label: this._labelProvider.transform("emailTemplatePlaceholders.resourceFirstSeen.label"),
                description: this._labelProvider.transform("emailTemplatePlaceholders.resourceFirstSeen.description"),
            },
            {
                label: this._labelProvider.transform("emailTemplatePlaceholders.resourceCreatedAt.label"),
                description: this._labelProvider.transform("emailTemplatePlaceholders.resourceCreatedAt.description"),
            },
            {
                label: this._labelProvider.transform("emailTemplatePlaceholders.linkToPlatform.label"),
                description: this._labelProvider.transform("emailTemplatePlaceholders.linkToPlatform.description"),
            },
        ];
    }

    public async setActionsForResources(
        resources: InventoryResourceModel[],
        inventoryResourcesCurrentPage: IInventoryResourceViewModel[],
    ): Promise<void> {
        const resourcesByIntegrationId = _.groupBy(resources, "integration.id");
        let flagCounter = 0;
        const maxFlagCounter = 100;
        Object.keys(resourcesByIntegrationId).forEach((integrationId) => {
            this._httpActionsServiceService
                .getActionsOfResources({
                    integrationId: integrationId,
                    resourcesIds: resourcesByIntegrationId[integrationId].map((resource) => resource.id),
                })
                .subscribe({
                    next: (response) => {
                        this._handleActionsResponse(integrationId, response, inventoryResourcesCurrentPage);
                        flagCounter = maxFlagCounter;
                    },
                });
        });
        while (flagCounter < maxFlagCounter) {
            flagCounter++;
            await new Promise((resolve) => setTimeout(resolve, 100));
        }
    }

    public handleActionClicked(action: InventorySupportedActions, inventoryResourceModel: InventoryResourceModel): Observable<boolean> {
        if (action == InventorySupportedActions.SendEmail) {
            return this._commonActionsService.sendEmailAction(this._createEmailData(inventoryResourceModel));
        }

        const actionsHttpParams = {
            integrationId: inventoryResourceModel.integration.id,
            resourceId: inventoryResourceModel.id,
            resourceType: inventoryResourceModel.resourceType,
            resourceStatus: inventoryResourceModel.resourceState.status,
        } as RequiredParamsForActionsHttpRequests;

        const optionalParams = {
            platformUrl: inventoryResourceModel.platformInfo.urls.value?.webAppUrl.value,
        };

        if (action == InventorySupportedActions.AddOwner) {
            return this._commonActionsService.addOrRemoveOwnerAction(actionsHttpParams, true, optionalParams);
        }
        if (action == InventorySupportedActions.RemoveOwner) {
            return this._commonActionsService.addOrRemoveOwnerAction(actionsHttpParams, false, optionalParams);
        }
        if (action == InventorySupportedActions.SetAccess) {
            const params = {
                ...actionsHttpParams,
                isChangeAccessActionEnabled: true,
            } as RequiredParamsForSetAccessHttpRequests;
            return this._commonActionsService.setAccessAction(params, optionalParams);
        }
        if (action == InventorySupportedActions.ListAccess) {
            const params = {
                ...actionsHttpParams,
                isChangeAccessActionEnabled: false,
            } as RequiredParamsForSetAccessHttpRequests;
            return this._commonActionsService.setAccessAction(params, optionalParams);
        }
        if (action == InventorySupportedActions.Delete) {
            return this._commonActionsService.deleteAction(inventoryResourceModel.displayName, actionsHttpParams);
        }
        if (action == InventorySupportedActions.Quarantine || action == InventorySupportedActions.Unquarantine) {
            const params = {
                ...actionsHttpParams,
                quarantine_status:
                    action == InventorySupportedActions.Quarantine
                        ? PpApplicationQuarantineStatus.quarantined
                        : PpApplicationQuarantineStatus.unquarantined,
            } as SetApplicationQuarantineStateParamsModel;
            return this._commonActionsService.setResourceQuarantineStateAction(inventoryResourceModel.displayName, params);
        }

        if (action === InventorySupportedActions.Start || action == InventorySupportedActions.Stop) {
            if (inventoryResourceModel.resourceType === ResourceType.recipe || inventoryResourceModel.resourceType === ResourceType.flow) {
                const params = {
                    ...actionsHttpParams,
                    action: action === InventorySupportedActions.Start ? AutomationActivationActions.start : AutomationActivationActions.stop,
                } as AutomationActivationParams;
                return this._commonActionsService.automationActivation(params, inventoryResourceModel.resourceType);
            }
        }
        if (
            action == InventorySupportedActions.GetResourceDefinition ||
            action == InventorySupportedActions.GetRawResource ||
            action == InventorySupportedActions.ChatGptEnterpriseGetUserCanvasAction
        ) {
            return this._commonActionsService.showRawData(actionsHttpParams);
        }
        if (action == InventorySupportedActions.PowerplatformGetRawFlowRuns) {
            return this._commonActionsService.OpenShowFlowRunsConfirmationDialogDialog(actionsHttpParams);
        }
        if (action == InventorySupportedActions.GetResourceSbomFile) {
            try {
                const sbom_attachment = this._commonActionsService.findSbomAttachment(inventoryResourceModel?.attachments);
                if (!sbom_attachment) {
                    // Since SBOM action shouldnt be available if there is no SBOM attachment, this should never happen
                    throw new Error(
                        `SBOM attachment not found for action: '${action}'. ${inventoryResourceModel.attachments.length} attachments found`,
                    );
                }
                return this._commonActionsService.getResourceAttachment(actionsHttpParams, sbom_attachment.id);
            } catch (error) {
                console.error(`InventoryActionsService handleActionClicked: ${action} failed.`, error);
                return of(false);
            }
        }
        if (action == InventorySupportedActions.PowerplatformSetCopilotAuthentication) {
            return this._commonActionsService.powerplatformSetCopilotAuthenticationAction(actionsHttpParams);
        }
        if (action == InventorySupportedActions.PowerplatformAgentDeleteAction) {
            return this._commonActionsService.powerplatformAgentDeleteAction(inventoryResourceModel.displayName, actionsHttpParams);
        }
        if (action == InventorySupportedActions.PowerplatformAgentActionDeleteAction) {
            return this._commonActionsService.powerplatformAgentActionDeleteAction(inventoryResourceModel.displayName, actionsHttpParams);
        }
        if (action == InventorySupportedActions.PowerplatformAgentActionDisableAction) {
            return this._commonActionsService.powerplatformAgentActionDisableAction(inventoryResourceModel.displayName, actionsHttpParams);
        }
        if (action == InventorySupportedActions.PowerplatformAgentActionEnforceUserAuthenticationAction) {
            return this._commonActionsService.powerplatformAgentActionEnforceUserAuthenticationAction(
                inventoryResourceModel.displayName,
                actionsHttpParams,
            );
        }
        if (action == InventorySupportedActions.PowerplatformAgentActionEnforceUserConfirmationAction) {
            return this._commonActionsService.powerplatformAgentActionEnforceUserConfirmationAction(
                inventoryResourceModel.displayName,
                actionsHttpParams,
            );
        }
        if (action == InventorySupportedActions.ChatGptEnterpriseDeleteGptAction) {
            return this._commonActionsService.chatGptEnterpriseDeleteGptAction(inventoryResourceModel.displayName, actionsHttpParams);
        }
        if (action == InventorySupportedActions.ChatGptEnterpriseIsolateGptAction) {
            return this._commonActionsService.chatGptEnterpriseIsolateGptAction(actionsHttpParams);
        }
        if (action == InventorySupportedActions.ChatGptEnterpriseDeleteGptKnowledgeAction) {
            return this._commonActionsService.chatGptEnterpriseDeleteGptKnowledgeAction(inventoryResourceModel.displayName, actionsHttpParams);
        }
        if (action == InventorySupportedActions.ChatGptEnterpriseGetGptKnowledgeFileUrlAction) {
            return this._commonActionsService.chatGptEnterpriseGetGptKnowledgeFileUrlAction(inventoryResourceModel.displayName, actionsHttpParams);
        }
        if (action == InventorySupportedActions.ChatGptEnterpriseDeleteUserCanvasAction) {
            return this._commonActionsService.chatGptEnterpriseDeleteUserCanvasAction(inventoryResourceModel.displayName, actionsHttpParams);
        }

        throw new Error(`InventoryActionsService handleActionClicked: ${action} support not implemented.`);
    }

    public getEmailPlaceholders(createdBySupported: boolean): EmailPlaceholder[] {
        const createdByPlaceholder: EmailPlaceholder[] = [
            {
                label: this._labelProvider.transform("emailTemplatePlaceholders.resourcesCreatedBy.label"),
                description: this._labelProvider.transform("emailTemplatePlaceholders.resourcesCreatedBy.description"),
            },
        ];

        if (createdBySupported) {
            return this._inventoryEmailPlaceholders.concat(createdByPlaceholder);
        }
        return this._inventoryEmailPlaceholders;
    }

    private _handleActionsResponse(
        integrationId: string,
        response: JsonHalCollection<ResourceActionsResponseModel>,
        inventoryResourcesCurrentPage: IInventoryResourceViewModel[],
    ): void {
        const resourcesActions = response._embedded.items;
        const resourcesIdsActionsDict: {
            [resourceId: string]: ResourceActionsResponseModel;
        } = {};
        resourcesActions.forEach((resourceActions) => {
            resourcesIdsActionsDict[resourceActions.resourceId] = resourceActions;
        });
        inventoryResourcesCurrentPage.forEach((resource) => {
            if (resource.resource.integration.id == integrationId && resource.resource.id in resourcesIdsActionsDict) {
                resource.menuActions = this._getMenuActionForInventoryResource(
                    resource.resource,
                    resourcesIdsActionsDict[resource.resource.id].actions,
                );
            }
        });
    }

    private _getMenuActionForInventoryResource(inventoryResourceModel: InventoryResourceModel, actions: ActionResponseModel[]): MenuAction[] {
        const resourceType = inventoryResourceModel.resourceType;
        const resourceStatus = inventoryResourceModel.resourceState.status;
        const newActions: MenuAction[] = [];
        actions.forEach((action) => {
            let flag = true;
            if (this._actionIdActionModelDictionary[action.id]) {
                const actionModel = this._actionIdActionModelDictionary[action.id];
                if (actionModel == InventorySupportedActions.Quarantine) {
                    const application_properties = inventoryResourceModel.resourceCategoryProperties as InventoryApplicationResourceProperties;
                    if (
                        application_properties !== undefined &&
                        application_properties.quarantineStatus.status === InventoryEntityFieldStatus.hasValue
                    ) {
                        const newAction =
                            application_properties.quarantineStatus.value === PowerPlatformApplicationQuarantineStatus.Unquarantined.toString()
                                ? InventorySupportedActions.Quarantine
                                : InventorySupportedActions.Unquarantine;
                        newActions.push(this._getMenuAction(newAction, resourceType, resourceStatus));
                    }
                    flag = false;
                }
                if (actionModel == InventorySupportedActions.Stop) {
                    // do not allow start/stop on suspended flow
                    if (inventoryResourceModel.resourceType == ResourceType.flow) {
                        const action = resourceStatus == ResourceStatus.active ? InventorySupportedActions.Stop : InventorySupportedActions.Start;
                        newActions.push(this._getMenuAction(action, resourceType, resourceStatus));
                    }
                    flag = false;
                }
                if (actionModel == InventorySupportedActions.ListAccess) {
                    const removeAccess = "removeResourcePermissions";
                    const actionsIds = actions.map((action) => action.id);
                    if (actionsIds.includes(removeAccess)) {
                        newActions.push(this._getMenuAction(InventorySupportedActions.SetAccess, resourceType, resourceStatus));
                    } else {
                        newActions.push(this._getMenuAction(InventorySupportedActions.ListAccess, resourceType, resourceStatus));
                    }
                    flag = false;
                }
                if (flag) newActions.push(this._getMenuAction(this._actionIdActionModelDictionary[action.id], resourceType, resourceStatus));
            }
        });
        // Our new API doesn't support all Powerplatform platform actions yet. Thus, we are taking the old code.
        if (inventoryResourceModel.platformInfo.platform == Platform.powerplatform) {
            if (this._commonActionsService.supportedAddOwnersResourceTypesArray.includes(resourceType)) {
                newActions.push(this._getMenuAction(InventorySupportedActions.AddOwner, resourceType, resourceStatus));
            }
            if (this._commonActionsService.supportedRemoveOwnersResourceTypesArray.includes(resourceType)) {
                newActions.push(this._getMenuAction(InventorySupportedActions.RemoveOwner, resourceType, resourceStatus));
            }
            if (this._commonActionsService.isGetDefinitionOptionVisible(Platform.powerplatform, resourceType)) {
                const integrationId = inventoryResourceModel.integration.id;
                if (integrationId) {
                    this._commonActionsService.isGetDefinitionOptionEnabled(integrationId).subscribe((value) => {
                        const disabledReason = value?.disabledReason;
                        const action = {
                            name: InventorySupportedActions.GetResourceDefinition.toString(),
                            displayNameLabel:
                                resourceType == ResourceType.flow ? "commonActions.getFlowDefinition" : "commonActions.getResourceDefinition",
                            disabled: disabledReason != undefined,
                            tooltip: disabledReason,
                        };
                        newActions.push(action);
                    });
                }
            }
        }
        // Our new API doesn't support Workato platform actions yet. Thus, we are taking the old code.
        if (inventoryResourceModel.platformInfo.platform == Platform.workato) {
            const integration = this._integrations.filter((integration) => integration.id == inventoryResourceModel.integration.id);
            if (integration.length == 1 && arePlatformActionsAllowed(Platform.workato, integration[0].authenticationMethod)) {
                if (resourceType == ResourceType.recipe) {
                    const action = resourceStatus === ResourceStatus.active ? InventorySupportedActions.Stop : InventorySupportedActions.Start;
                    newActions.push(this._getMenuAction(action, resourceType, resourceStatus));

                    newActions.push(this._getMenuAction(InventorySupportedActions.Delete, resourceType, resourceStatus));
                }
            }
        }
        newActions.push(this._getMenuAction(InventorySupportedActions.SendEmail, resourceType, resourceStatus));
        return newActions;
    }

    private _getMenuAction(
        supportedAction: InventorySupportedActions,
        resourceType: ResourceType,
        inventoryResourceStatus: ResourceStatus,
    ): MenuAction {
        let hasPermissions;
        const isDeleted = this._isResourceDeleted(inventoryResourceStatus);

        switch (supportedAction) {
            case InventorySupportedActions.AddOwner:
                hasPermissions = this._permissionsService.hasPermissions(
                    this._commonActionsService.getPermissionRequiredForResourceAction(resourceType),
                );
                return {
                    name: InventorySupportedActions.AddOwner.toString(),
                    displayNameLabel: "commonActions.addOwner",
                    disabled: !hasPermissions || isDeleted,
                    tooltip: !hasPermissions
                        ? this._labelProvider.transform("commonActions.adminActionTooltip")
                        : isDeleted
                          ? this._labelProvider.transform("commonActions.deletedResourceTooltip")
                          : "",
                } as MenuAction;
            case InventorySupportedActions.RemoveOwner:
                hasPermissions = this._permissionsService.hasPermissions(
                    this._commonActionsService.getPermissionRequiredForResourceAction(resourceType),
                );
                return {
                    name: InventorySupportedActions.RemoveOwner.toString(),
                    displayNameLabel: "commonActions.removeOwner",
                    disabled: !hasPermissions || isDeleted,
                    tooltip: !hasPermissions
                        ? this._labelProvider.transform("commonActions.adminActionTooltip")
                        : isDeleted
                          ? this._labelProvider.transform("commonActions.deletedResourceTooltip")
                          : "",
                } as MenuAction;
            case InventorySupportedActions.ListAccess:
                return {
                    name: InventorySupportedActions.ListAccess.toString(),
                    displayNameLabel: "commonActions.setAccess",
                    disabled: isDeleted,
                } as MenuAction;
            case InventorySupportedActions.SetAccess:
                return {
                    name: InventorySupportedActions.SetAccess.toString(),
                    displayNameLabel: "commonActions.setAccess",
                    disabled: isDeleted,
                } as MenuAction;
            case InventorySupportedActions.Delete:
                hasPermissions = this._permissionsService.hasPermissions(PermissionEnum.admin);
                return {
                    name: InventorySupportedActions.Delete.toString(),
                    displayNameLabel: "commonActions.delete",
                    disabled: !this._permissionsService.hasPermissions(PermissionEnum.admin) || isDeleted,
                    tooltip: !hasPermissions
                        ? this._labelProvider.transform("commonActions.adminActionTooltip")
                        : isDeleted
                          ? this._labelProvider.transform("commonActions.deletedResourceTooltip")
                          : "",
                } as MenuAction;
            case InventorySupportedActions.SendEmail:
                hasPermissions = this._permissionsService.hasPermissions(PermissionEnum.operator);
                return {
                    name: InventorySupportedActions.SendEmail.toString(),
                    displayNameLabel: "commonActions.sendEmail",
                    disabled: !hasPermissions,
                    tooltip: !hasPermissions ? this._labelProvider.transform("commonActions.adminActionTooltip") : "",
                } as MenuAction;
            case InventorySupportedActions.Quarantine:
                hasPermissions = this._permissionsService.hasPermissions(
                    this._commonActionsService.getPermissionRequiredForResourceAction(resourceType),
                );
                return {
                    name: InventorySupportedActions.Quarantine.toString(),
                    displayNameLabel: "commonActions.quarantine",
                    disabled: !hasPermissions || isDeleted,
                    tooltip: !hasPermissions
                        ? this._labelProvider.transform("commonActions.adminActionTooltip")
                        : isDeleted
                          ? this._labelProvider.transform("commonActions.deletedResourceTooltip")
                          : "",
                } as MenuAction;
            case InventorySupportedActions.Unquarantine:
                hasPermissions = this._permissionsService.hasPermissions(
                    this._commonActionsService.getPermissionRequiredForResourceAction(resourceType),
                );
                return {
                    name: InventorySupportedActions.Unquarantine.toString(),
                    displayNameLabel: "commonActions.unquarantine",
                    disabled: !hasPermissions || isDeleted,
                    tooltip: !hasPermissions
                        ? this._labelProvider.transform("commonActions.adminActionTooltip")
                        : isDeleted
                          ? this._labelProvider.transform("commonActions.deletedResourceTooltip")
                          : "",
                } as MenuAction;
            case InventorySupportedActions.Start:
                hasPermissions = this._permissionsService.hasPermissions(PermissionEnum.operator);
                return {
                    name: InventorySupportedActions.Start.toString(),
                    displayNameLabel: "commonActions.start",
                    disabled: !hasPermissions,
                    tooltip: !hasPermissions ? this._labelProvider.transform("commonActions.operatorActionTooltip") : "",
                } as MenuAction;
            case InventorySupportedActions.Stop:
                hasPermissions = this._permissionsService.hasPermissions(PermissionEnum.operator);
                return {
                    name: InventorySupportedActions.Stop.toString(),
                    displayNameLabel: "commonActions.stop",
                    disabled: !hasPermissions,
                    tooltip: !hasPermissions ? this._labelProvider.transform("commonActions.operatorActionTooltip") : "",
                } as MenuAction;
            case InventorySupportedActions.GetResourceSbomFile:
                return {
                    name: InventorySupportedActions.GetResourceSbomFile.toString(),
                    displayNameLabel: "commonActions.getResourceSbomFile",
                    disabled: isDeleted,
                } as MenuAction;
            case InventorySupportedActions.GetRawResource:
                return {
                    name: InventorySupportedActions.GetRawResource.toString(),
                    displayNameLabel: "commonActions.getRawResourceAction.actionName",
                    disabled: isDeleted,
                } as MenuAction;
            case InventorySupportedActions.PowerplatformSetCopilotAuthentication:
                return {
                    name: InventorySupportedActions.PowerplatformSetCopilotAuthentication.toString(),
                    displayNameLabel: "commonActions.powerplatformSetCopilotAuthentication.actionName",
                    disabled: isDeleted,
                } as MenuAction;

            case InventorySupportedActions.PowerplatformGetRawFlowRuns:
                return {
                    name: InventorySupportedActions.PowerplatformGetRawFlowRuns.toString(),
                    displayNameLabel: "commonActions.getFlowRunsAction.displayName",
                    disabled: isDeleted,
                } as MenuAction;
            case InventorySupportedActions.PowerplatformAgentDeleteAction:
                return {
                    name: InventorySupportedActions.PowerplatformAgentDeleteAction.toString(),
                    displayNameLabel: "commonActions.delete",
                    disabled: isDeleted,
                } as MenuAction;
            case InventorySupportedActions.PowerplatformAgentActionDeleteAction:
                return {
                    name: InventorySupportedActions.PowerplatformAgentActionDeleteAction.toString(),
                    displayNameLabel: "commonActions.delete",
                    disabled: isDeleted,
                } as MenuAction;
            case InventorySupportedActions.PowerplatformAgentActionDisableAction:
                return {
                    name: InventorySupportedActions.PowerplatformAgentActionDisableAction.toString(),
                    displayNameLabel: "commonActions.disable",
                    disabled: isDeleted,
                } as MenuAction;
            case InventorySupportedActions.PowerplatformAgentActionEnforceUserAuthenticationAction:
                return {
                    name: InventorySupportedActions.PowerplatformAgentActionEnforceUserAuthenticationAction.toString(),
                    displayNameLabel: "commonActions.agentActionEnforceUserAuthentication",
                    disabled: isDeleted,
                } as MenuAction;
            case InventorySupportedActions.PowerplatformAgentActionEnforceUserConfirmationAction:
                return {
                    name: InventorySupportedActions.PowerplatformAgentActionEnforceUserConfirmationAction.toString(),
                    displayNameLabel: "commonActions.agentActionEnforceUserConfirmation",
                    disabled: isDeleted,
                } as MenuAction;
            case InventorySupportedActions.ChatGptEnterpriseDeleteGptAction:
                return {
                    name: InventorySupportedActions.ChatGptEnterpriseDeleteGptAction.toString(),
                    displayNameLabel: "commonActions.delete",
                    disabled: isDeleted,
                } as MenuAction;
            case InventorySupportedActions.ChatGptEnterpriseIsolateGptAction:
                return {
                    name: InventorySupportedActions.ChatGptEnterpriseIsolateGptAction.toString(),
                    displayNameLabel: "commonActions.isolate",
                    disabled: isDeleted,
                } as MenuAction;
            case InventorySupportedActions.ChatGptEnterpriseDeleteGptKnowledgeAction:
                return {
                    name: InventorySupportedActions.ChatGptEnterpriseDeleteGptKnowledgeAction.toString(),
                    displayNameLabel: "commonActions.delete",
                    disabled: isDeleted,
                } as MenuAction;
            case InventorySupportedActions.ChatGptEnterpriseGetGptKnowledgeFileUrlAction:
                return {
                    name: InventorySupportedActions.ChatGptEnterpriseGetGptKnowledgeFileUrlAction.toString(),
                    displayNameLabel: "commonActions.getGptKnowledge.title",
                    disabled: isDeleted,
                } as MenuAction;
            case InventorySupportedActions.ChatGptEnterpriseDeleteUserCanvasAction:
                return {
                    name: InventorySupportedActions.ChatGptEnterpriseDeleteUserCanvasAction.toString(),
                    displayNameLabel: "commonActions.delete",
                    disabled: isDeleted,
                } as MenuAction;
            case InventorySupportedActions.ChatGptEnterpriseGetUserCanvasAction:
                return {
                    name: InventorySupportedActions.ChatGptEnterpriseGetUserCanvasAction.toString(),
                    displayNameLabel: "commonActions.getUserCanvas.title",
                    disabled: isDeleted,
                } as MenuAction;
            default:
                throw new Error(`InventoryActionsService getMenuAction: The action ${supportedAction} is not supported`);
        }
    }

    private _isResourceDeleted(inventoryResourceStatus: ResourceStatus): boolean {
        return inventoryResourceStatus === ResourceStatus.pendingDeletion;
    }

    private _createEmailData(inventoryResourceModel: InventoryResourceModel): EmailData {
        const resourceMakerEmails = this._commonActionsService.getResourceMakerEmails(
            inventoryResourceModel.identities.value?.ownedBy,
            inventoryResourceModel.identities.value?.createdBy,
        );
        const platformDisplayName = getPlatformDisplayName(inventoryResourceModel.platformInfo.platform);
        const resourceTypeDisplayName = beautifyCamelCaseString(inventoryResourceModel.resourceType);
        const createdBy = inventoryResourceModel.identities.value?.createdBy.value?.displayName;
        const platformLink = inventoryResourceModel.platformInfo.urls.value?.webAppUrl.value;
        const body = `Hi,

This is an automated email notifying you that our scans captured information about "${inventoryResourceModel.displayName}".

Here is more information about the resource:
* "${inventoryResourceModel.displayName}" is a ${resourceTypeDisplayName} in ${platformDisplayName}.
* It was created at  ${formatFromDate(inventoryResourceModel.platformInfo.creationTime.value!)} and was first seen at ${formatFromDate(inventoryResourceModel.platformInfo.firstSeen)}.
${createdBy ? `* Created by ${createdBy}` : ""}

To get more information about the Resource in Zenity, go to  ${environment.app_base_url}/app/inventory?integration=${inventoryResourceModel.integration.id}&resource=${inventoryResourceModel.id}.

To view the resource in the source platform, go to ${platformLink}

For more information or questions please reach out to us at support@zenity.io`;

        return {
            to: "",
            subject: `Automated Message - ${platformDisplayName} ${resourceTypeDisplayName} details`,
            integrationId: inventoryResourceModel.integration.id,
            body: body,
            resourceId: inventoryResourceModel.id,
            resourceMakerEmails: resourceMakerEmails,
            emailPlaceholders: this.getEmailPlaceholders(createdBy !== undefined),
        } as EmailData;
    }
}
